import React from "react";
import { createRoot } from "react-dom/client";
import { useState, useEffect } from "react";
import "./index.css";
import { SplashScreen } from "./components/SplashScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { logger } from "./utils/logger";
import { initializeSecurity } from "./lib/security";
import { initNetworkQuality } from "./lib/networkQuality";
import { initWebVitals } from "./lib/vitals";
import { getLastRoute } from "./lib/lastRoute";

// NOTE: App + auth/cloud providers are imported lazily inside Root so any
// boot-time module error (e.g. missing Firebase env var) surfaces in the
// ErrorBoundary instead of crashing the bundle before React mounts (blank screen).
const App = React.lazy(() => import("./App.tsx"));
const AuthOrchestratorProvider = React.lazy(() =>
  import("./contexts/AuthOrchestratorContext").then((m) => ({ default: m.AuthOrchestratorProvider }))
);
const OnboardingProvider = React.lazy(() =>
  import("./contexts/OnboardingContext").then((m) => ({ default: m.OnboardingProvider }))
);
const BusinessAccessProvider = React.lazy(() =>
  import("./contexts/BusinessAccessContext").then((m) => ({ default: m.BusinessAccessProvider }))
);
const ModeProviderWrapper = React.lazy(() =>
  import("./components/ModeProviderWrapper").then((m) => ({ default: m.ModeProviderWrapper }))
);
const AppProvider = React.lazy(() =>
  import("./contexts/AppContext").then((m) => ({ default: m.AppProvider }))
);

// Initialize security features early
initializeSecurity();

// Tag <html> with current network speed so CSS Save-Data branch can engage.
initNetworkQuality();

// Web Vitals reporter (page-scoped, additive — never throws).
initWebVitals();

// Modulepreload the last-visited route's chunk so returning users see it
// render almost instantly. Vite resolves the URL via the bundled glob map.
try {
  const last = getLastRoute();
  if (last) {
    const pageModules = import.meta.glob('./pages/**/*.tsx');
    // Map common route paths to their page file (best-effort, safe fallback).
    const routeToFile: Record<string, string> = {
      '/home': './pages/Index.tsx',
      '/growth': './pages/Growth.tsx',
      '/learning': './pages/Learning.tsx',
      '/profile': './pages/Profile.tsx',
      '/following-feed': './pages/FollowingFeed.tsx',
      '/following': './pages/FollowingFeed.tsx',
      '/shop': './pages/Shop.tsx',
      '/budgets': './pages/Budgets.tsx',
      '/investments': './pages/Investments.tsx',
      '/freebusiness': './pages/FreeBusiness.tsx',
      '/business-dashboard': './pages/BusinessDashboard.tsx',
      '/chat': './pages/EnhancedChat.tsx',
      '/credits': './pages/InvisaCredits.tsx',
    };
    const key = routeToFile[last];
    const loader = key && pageModules[key];
    if (loader) {
      const idle: (cb: () => void) => number =
        (window as any).requestIdleCallback?.bind(window) ??
        ((cb: () => void) => window.setTimeout(cb, 100));
      idle(() => { (loader as () => Promise<unknown>)().catch(() => {}); });
    }
  }
} catch {
  /* preload is best-effort only */
}

// Capacitor deep-link handling (only registered on native platforms)
import { App as CapacitorApp, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
// Note: '@capacitor-community/facebook-login' is dynamically imported below,
// only on native platforms, to keep it out of the web bundle (it's externalized in vite.config.ts).
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Ensure React is available globally for all modules
if (typeof window !== 'undefined') {
  (window as any).React = React;
}

// Global error handlers
window.addEventListener('error', (event) => {
  logger.error('Global error caught:', event.error);
  console.error('🔴 UNCAUGHT ERROR:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled promise rejection:', event.reason);
  console.error('🔴 UNHANDLED PROMISE REJECTION:', event.reason);
});

function Root() {
  const [showSplash, setShowSplash] = useState(true);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [diagMessage, setDiagMessage] = useState<string | null>(null);

  const handleSplashComplete = () => {
    logger.info('Splash screen complete, loading app');
    setShowSplash(false);
  };

  useEffect(() => {
    logger.info('Root component mounted');
    
    // Apply saved theme or default to dark mode for best experience
    const savedPreference = localStorage.getItem('theme-preference');
    let appliedTheme: string;
    
    if (!savedPreference) {
      // First time user - default to dark mode
      appliedTheme = 'dark';
      localStorage.setItem('theme-preference', 'dark');
    } else if (savedPreference === 'system') {
      // User explicitly chose to follow system
      appliedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      // User has explicitly set light or dark
      appliedTheme = savedPreference;
    }
    
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(appliedTheme);
  }, []);

  // If the app is still stuck on the splash after 10s, show a diagnostic screen
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (showSplash) {
        console.error('🟠 Startup timeout: still on splash after 10s — showing diagnostic UI');
        setDiagMessage('Startup timeout: application still loading after 10s. Showing diagnostics below.');
        setShowDiagnostic(true);
        setShowSplash(false);
      }
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [showSplash]);

  // Initialize Facebook SDK for native platforms (dynamic import — kept out of web bundle)
  useEffect(() => {
    if (typeof Capacitor !== 'undefined' && (Capacitor as any).isNativePlatform && (Capacitor as any).isNativePlatform()) {
      import('@capacitor-community/facebook-login')
        .then(({ FacebookLogin }) =>
          FacebookLogin.initialize({ appId: '805290095704205' })
        )
        .then(() => console.log('Facebook SDK initialized'))
        .catch((error) => console.error('Facebook SDK initialization failed:', error));
    }
  }, []);

  // Register deep link handler for native OAuth callbacks
  useEffect(() => {
    try {
      if (typeof Capacitor !== 'undefined' && (Capacitor as any).isNativePlatform && (Capacitor as any).isNativePlatform()) {
        const handler = async (event: any) => {
          try {
            // Support both web-like URLs and custom scheme deep links (invisa://auth?...)
            const urlStr = event.url;
            let parsed: URL;

            // Some platforms give custom-scheme URLs which are not valid with new URL()
            // Normalize custom scheme to a pseudo-URL that can be parsed
            if (typeof urlStr === 'string' && urlStr.startsWith('invisa://')) {
              // Convert invisa://auth?code=... to https://invisa.local/auth?code=... for parsing
              parsed = new URL(urlStr.replace('invisa://', 'https://invisa.local/'));
            } else {
              parsed = new URL(urlStr);
            }

            const code = parsed.searchParams.get('code');
            const err = parsed.searchParams.get('error');

            if (err) {
              console.error('OAuth error in callback URL:', err);
              window.location.href = `/auth/callback?error=${encodeURIComponent(err)}`;
              return;
            }

            if (code) {
              // Firebase handles OAuth token exchange internally via Google Sign-In SDK
              // No manual PKCE code exchange needed with Firebase
              console.log('OAuth code received (Firebase handles internally)');
            }
          } catch (e) {
            console.error('Deep link handling error:', e);
            window.location.href = `/auth/callback?error=${encodeURIComponent('deep_link_failed')}`;
          }
        };

        let listenerHandle: PluginListenerHandle | null = null;
        CapacitorApp.addListener('appUrlOpen', handler).then(handle => {
          listenerHandle = handle;
        });
        return () => {
          listenerHandle?.remove();
        };
      }
    } catch (e) {
      console.warn('Deep link handler not registered (non-native environment)');
    }
  }, []);

  return (
    <ErrorBoundary>
      <React.Suspense fallback={<SplashScreen onComplete={() => {}} />}>
        <AuthOrchestratorProvider>
          <OnboardingProvider>
            <AppProvider>
              <BusinessAccessProvider>
                <ModeProviderWrapper>
                  {showDiagnostic ? (
                    <div style={{padding:20, color:'#fff', background:'#0b1220', minHeight: '100vh'}}>
                      <h2 style={{color:'#f43f5e'}}>Startup Diagnostic</h2>
                      <p>{diagMessage}</p>
                      <pre style={{whiteSpace:'pre-wrap', fontSize:12, background:'#071022', padding:12, borderRadius:8}}>
{`User agent: ${navigator.userAgent}
Capacitor native: ${Boolean((window as any).Capacitor?.isNativePlatform?.())}
Supabase URL: ${String(import.meta.env.VITE_SUPABASE_URL ?? 'MISSING').replace(/https?:\/\//, '')}
Supabase Key present: ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY ? 'YES' : 'NO'}`}
                      </pre>
                      <div style={{marginTop:12}}>
                        <button onClick={() => location.reload()} style={{marginRight:12, padding:'8px 12px'}}>Reload</button>
                      </div>
                    </div>
                  ) : showSplash ? (
                    <SplashScreen onComplete={handleSplashComplete} />
                  ) : (
                    <App />
                  )}
                </ModeProviderWrapper>
              </BusinessAccessProvider>
            </AppProvider>
          </OnboardingProvider>
        </AuthOrchestratorProvider>
      </React.Suspense>
    </ErrorBoundary>
  );
}

// Register service worker for offline support and push notifications.
// Deferred to idle after `load` so it never competes with first paint.
// Skipped inside Capacitor — the WebView already serves assets from the APK,
// and a stale SW intercepting navigation is a common cause of blank screens
// after a Play Store install/update.
const isNativeCapacitor =
  typeof (window as any).Capacitor?.isNativePlatform === 'function'
    ? (window as any).Capacitor.isNativePlatform()
    : !!(window as any).Capacitor?.isNative;

if ('serviceWorker' in navigator) {
  if (isNativeCapacitor) {
    // Unregister any SW left over from a previous build so the WebView
    // never serves a stale cached shell.
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .catch(() => {});
  } else {
    window.addEventListener('load', () => {
      const idle: (cb: () => void) => number =
        (window as any).requestIdleCallback?.bind(window) ??
        ((cb: () => void) => window.setTimeout(cb, 500));
      idle(() => {
        // Use VitePWA virtual module for registration
        import('virtual:pwa-register').then(({ registerSW }) => {
          registerSW({
            onNeedRefresh() {
              console.log('🔄 New content available, updating service worker...');
            },
            onOfflineReady() {
              console.log('✅ App is ready for offline usage.');
              logger.info('Service Worker ready for offline usage');
            },
            onRegistered(r: any) {
              console.log('✅ Service Worker registered via VitePWA:', r);
            },
            onRegisterError(err: any) {
              console.error('❌ Service Worker registration failed:', err);
              logger.error('Service Worker registration failed', err);
            },
          });
        }).catch(err => {
          console.error('Failed to import virtual:pwa-register', err);
        });
      });
    });
  }
}


logger.info('Initializing React application');
console.log('🚀 Starting application...');
// Log important startup environment values (mask sensitive values)
try {
  const envSummary = {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ? String(import.meta.env.VITE_SUPABASE_URL).replace(/https?:\/\//, '') : 'MISSING',
    VITE_SUPABASE_KEY_PRESENT: Boolean(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY),
    MODE: import.meta.env.MODE,
  };
  console.log('🔐 Startup env summary:', envSummary);
} catch (e) {
  console.warn('Could not read startup env summary', e);
}

try {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error('Root element not found in DOM');
  }
  console.log('✅ Root element found, creating React root');
  createRoot(rootElement).render(<Root />);
  console.log('✅ React root rendered successfully');
} catch (error) {
  logger.error('Failed to initialize React app', error);
  console.error('🔴 CRITICAL: Failed to render React app:', error);
  document.body.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #0f172a; color: white; font-family: system-ui; padding: 20px;">
      <div style="max-width: 600px; background: #1e293b; padding: 30px; border-radius: 12px; border: 2px solid #ef4444;">
        <h1 style="color: #ef4444; margin: 0 0 20px 0;">❌ Critical Initialization Error</h1>
        <p style="margin: 0 0 15px 0;">The application failed to start. Error details:</p>
        <pre style="background: #0f172a; padding: 15px; border-radius: 6px; overflow-x: auto; font-size: 12px;">${error instanceof Error ? error.message : String(error)}</pre>
        <button onclick="location.reload()" style="margin-top: 20px; padding: 12px 24px; background: #3b82f6; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600;">Reload Page</button>
      </div>
    </div>
  `;
}
